import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Briefcase, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  ArrowLeft,
  Send,
  FileText,
  CheckCircle
} from 'lucide-react'
import { useStore } from '../store/useStore'
import Button from '../components/ui/Button'
import GlassCard from '../components/ui/GlassCard'
import Section from '../components/ui/Section'

const JobDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { jobPostings } = useStore()
  const [job, setJob] = useState(null)
  const [isApplying, setIsApplying] = useState(false)
  const [applicationSent, setApplicationSent] = useState(false)

  useEffect(() => {
    const foundJob = jobPostings.find(j => j.id === id)
    if (foundJob) {
      setJob(foundJob)
    } else {
      navigate('/')
    }
  }, [id, jobPostings, navigate])

  const handleApply = async () => {
    setIsApplying(true)
    
    // Simuler l'envoi de candidature
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    setIsApplying(false)
    setApplicationSent(true)
    
    // Rediriger après 3 secondes
    setTimeout(() => {
      navigate('/')
    }, 3000)
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  if (applicationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Candidature Envoyée !</h2>
          <p className="text-gray-600 mb-4">
            Votre candidature pour le poste de <strong>{job.title}</strong> a été envoyée avec succès.
          </p>
          <p className="text-sm text-gray-500">
            Nous vous contacterons sous peu. Redirection en cours...
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Section className="py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bouton Retour */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowLeft size={16} />
              <span>Retour à l'accueil</span>
            </Button>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <GlassCard className="p-8">
                {/* En-tête du poste */}
                <div className="mb-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
                      <div className="flex items-center space-x-4 text-gray-600">
                        <div className="flex items-center space-x-1">
                          <MapPin size={16} />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar size={16} />
                          <span>Publié le {new Date(job.createdAt).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={16} />
                          <span>{job.type}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600 mb-1">{job.salary}</div>
                      <div className="text-sm text-gray-500">Salaire</div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText size={20} className="mr-2" />
                    Description du Poste
                  </h2>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed">{job.description}</p>
                  </div>
                </div>

                {/* Exigences */}
                {job.requirements && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Users size={20} className="mr-2" />
                      Exigences
                    </h2>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {job.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Avantages */}
                {job.benefits && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Avantages</h2>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {job.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Bouton Candidature */}
                <div className="text-center pt-6 border-t border-gray-200">
                  <Button
                    onClick={handleApply}
                    disabled={isApplying}
                    className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center space-x-2 mx-auto"
                  >
                    {isApplying ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Postuler Maintenant</span>
                      </>
                    )}
                  </Button>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </Section>
    </div>
  )
}

export default JobDetails
